import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import Cookies from 'js-cookie'
import _ from 'lodash'
import classNames from 'classnames/bind'
import { ProductLine, useApplicationTheme } from '@adc/ui-core'
import { InvestorPreferences } from '../../../util'
import { FiltersModel } from '../../../models'
import { AUTH_INTENT_KINDS, USER_KINDS } from '../constants.js'
import LoginForm from './login'
import SignUpForm from './signup'
import StepForm from './stepForm'
import {
  OnboardingHeader,
  ThankYouPage,
  OnboardingQuestion,
} from './userOnboardingQuestions'
import { gtmAuthSuccess } from '../../../shared/services/tracking'
import cls from './styles.scss'
const cx = classNames.bind(cls)

const WAIT_BEFORE_FOCUS = 100
const QUESTION_SETS = InvestorPreferences.IP_OBJECT()
const OCCUPIED_PROPERTIES_QUESTION =
  InvestorPreferences.IP_QUESTION_CODE.INV_PREF_OCCUPIED_PROPERTIES

const REMEMBER_EMAIL_COOKIE_NAME = 'ADC_LGN_EML'
const FORCE_LOGIN_COOKIE_NAME = 'AUTH_SHOW_LOGIN'
const COOKIE_EXPIRATION_DAYS = 365

const DEFAULT_THEME = {
  productLine: ProductLine.AuctionCom,
  version: 1,
}

export const hasPostSignUpSteps = (userKind, theme = DEFAULT_THEME) =>
  userKind === USER_KINDS.CONSUMER &&
  theme.productLine !== ProductLine.SmartSale

export const AuthToggleForm = props => {
  const {
    elmId = 'auth_toggle',
    loginProps,
    signupProps,
    updateSavedSearches,
    onClose,
    cls: extcls = {},
    featureFlags = {},
    authIntent = '',
    signUpInitialValues = {},
    onAuthToggle = _.noop,
    userKind = USER_KINDS.CONSUMER,
    setUser = () => {},
    onSuccess = _.noop,
    nextStep = () => {},
    onFail = _.noop,
    additionalFields = null,
    initialActive = 'login',
  } = props

  const theme = useApplicationTheme()

  const [state, setState] = useState({
    rememberEmail: false,
    userEmail: props.userEmail,
    authType: initialActive,
  })

  const rememberEmailCookieName = useMemo(
    () =>
      _.get(
        props,
        'loginProps.rememberEmailCookieName',
        REMEMBER_EMAIL_COOKIE_NAME
      ),
    [props]
  )

  useEffect(() => {
    const userEmail = Cookies.get(rememberEmailCookieName)
    if (userEmail) {
      setState(prevState => ({
        ...prevState,
        userEmail,
        rememberEmail: true,
      }))
    }
  }, [rememberEmailCookieName])

  const setAuthType = authType => {
    onAuthToggle(authType)
    setState(prevState => ({
      ...prevState,
      authType,
    }))
  }

  const switchToLogin = email => {
    setAuthType('login')
    setState(prevState => ({
      ...prevState,
      userEmail: email,
    }))

    setTimeout(() => {
      if (email) {
        // set focus on password field
        const passwordField = document.querySelector(
          `[data-elm-id="${elmId}_login_password_input"] input`
        )
        if (passwordField) {
          passwordField.focus()
        }
      }
    }, WAIT_BEFORE_FOCUS)
  }

  const onAuthSuccess = user => {
    // set cookie when authenticated
    Cookies.set(FORCE_LOGIN_COOKIE_NAME, 'y', {
      expires: COOKIE_EXPIRATION_DAYS,
    })
    const authenticatedUser = { ...user, logged_in: true }
    const eventName =
      state.authType === 'signup' ? 'account-create' : 'account-login'
    gtmAuthSuccess(eventName, authenticatedUser, { intent: authIntent })
    if (hasPostSignUpSteps(userKind, theme) && state.authType === 'signup') {
      setUser(authenticatedUser)
      onSuccess(state.authType, authenticatedUser)
      nextStep()
      return Promise.resolve()
    } else {
      return onSuccess(state.authType, authenticatedUser)
    }
  }

  const onAuthFail = error => {
    onFail(error)
  }

  const { authType, userEmail, rememberEmail } = state
  const loginActive = authType === 'login'
  const signupActive = authType === 'signup'
  const loginFormInitialValues = {
    email: userEmail,
    rememberEmail,
  }

  return (
    <div data-elm-id={elmId} className={cx('root', extcls.root)}>
      <div className={cx('tabbed-toggle')}>
        <div className={cx('tabbed-container', loginActive && 'active')}>
          <h3
            data-elm-id={`${elmId}_tabbed_login_link`}
            onClick={setAuthType.bind(this, 'login')}
            tabIndex='0'
            className={cx('tabbed-text', loginActive && 'active')}
          >
            Log In
          </h3>
        </div>
        <div className={cx('tabbed-container', signupActive && 'active')}>
          <h3
            data-elm-id={`${elmId}_tabbed_signup_link`}
            onClick={setAuthType.bind(this, 'signup')}
            tabIndex='0'
            className={cx('tabbed-text', signupActive && 'active')}
          >
            Sign Up
          </h3>
        </div>
      </div>

      {loginActive && (
        <LoginForm
          initialValues={loginFormInitialValues}
          onFail={onAuthFail}
          onSuccess={onAuthSuccess}
          elmId={`${elmId}_login`}
          {...loginProps}
          rememberEmailCookieName={rememberEmailCookieName}
          authIntent={authIntent}
          additionalFields={additionalFields}
        />
      )}
      {signupActive && (
        <SignUpForm
          onFail={onAuthFail}
          onSuccess={onAuthSuccess}
          elmId={`${elmId}_signup`}
          initialValues={{ ...signUpInitialValues, emailOptIn: true }}
          saveSearch
          shouldRequirePhone={false}
          updateSavedSearches={updateSavedSearches}
          userKind={userKind}
          {...signupProps}
          authIntent={authIntent}
          abnTest={
            !_.isEmpty(
              featureFlags['fe.resi.ab.testing.signup-propositions']
            ) && featureFlags['fe.resi.ab.testing.signup-propositions']
          }
          notificationSettingsRedesignEnabled={
            !!featureFlags['fe.resi.enable-notification-settings-redesign']
          }
          switchToLogin={switchToLogin}
        />
      )}

      {onClose && (
        <div className={cx('not-now')}>
          <a target='_blank' onClick={onClose} data-elm-id={`${elmId}_not_now`}>
            Not Now
          </a>
        </div>
      )}
    </div>
  )
}

AuthToggleForm.propTypes = {
  cls: PropTypes.object,
  elmId: PropTypes.string,
  onAuthToggle: PropTypes.func,
  onSuccess: PropTypes.func.isRequired,
  onFail: PropTypes.func,
  initialActive: PropTypes.oneOf(['login', 'signup']),
  updateSavedSearches: PropTypes.func,
  userEmail: PropTypes.string,
  loginProps: PropTypes.object,
  signupProps: PropTypes.object,
  featureFlags: PropTypes.object,
  onClose: PropTypes.func,
  nextStep: PropTypes.func.isRequired,
  setUser: PropTypes.func.isRequired,
  userKind: PropTypes.oneOf(Object.values(USER_KINDS)),
  authIntent: PropTypes.oneOf(Object.values(AUTH_INTENT_KINDS)),
  signUpInitialValues: PropTypes.object,
  additionalFields: PropTypes.node,
}

const AuthForm = ({ currentPage, ...pageProps }) => (
  <AuthToggleForm
    {...currentPage.authProps}
    {...pageProps}
    updateSavedSearches={currentPage.updateSavedSearches}
    setUser={currentPage.setUser}
  />
)

AuthForm.propTypes = {
  currentPage: PropTypes.object,
}

const AuthToggleStepForm = ({
  redirectToSearch = false,
  userKind = USER_KINDS.CONSUMER,
  signUpInitialValues = {},
  ...authProps
}) => {
  const theme = useApplicationTheme()
  const [savedSearches, updateSavedSearches] = useState([])
  const [user, setUser] = useState({})

  const successfulSignup = () => authProps.onSignupFlowFinish()

  if (!hasPostSignUpSteps(userKind, theme)) {
    return (
      <AuthToggleForm
        {...authProps}
        userKind={userKind}
        signUpInitialValues={signUpInitialValues}
      />
    )
  }

  const redirectToSavedSearch = () => {
    // Drive the redirect from the in-memory seed search (interested areas is a
    // required field, so there is normally at least one). Guard against an
    // empty list so we never throw — fall back to the standard signup finish.
    const params = savedSearches[0]?.parameters
    if (!params) {
      successfulSignup()
      return
    }
    const model = new FiltersModel(params)
    const redirectUrl = model.urlValue
    window.location.assign(`/residential/${redirectUrl}`)
    successfulSignup()
  }
  const showPrivateSellerOption =
    authProps.featureFlags['fe.resi.investors-preference-variant-enabled']
  const originalOptions = QUESTION_SETS.INV_PREF_PROPERTY_TYPE.options

  const filteredOptions = originalOptions.filter(option => {
    if (showPrivateSellerOption) {
      return option.value !== 'BOTH'
    } else {
      return option.value !== 'PRIVATE_SELLER' && option.value !== 'ALL'
    }
  })
  const updatedObject = {
    ...(QUESTION_SETS.INV_PREF_PROPERTY_TYPE || {}),
    options: filteredOptions,
  }
  const pages = [
    {
      authProps,
      setUser,
      updateSavedSearches,
      component: AuthForm,
    },
    {
      ...updatedObject,
      component: pageProps => (
        <OnboardingQuestion
          user={user}
          elmId='property_type_question' // Use a unique ID
          savedSearches={savedSearches}
          {...pageProps}
        />
      ),
    },
    {
      ...QUESTION_SETS[OCCUPIED_PROPERTIES_QUESTION],
      component: pageProps => (
        <OnboardingQuestion
          user={user}
          elmId='second_question'
          savedSearches={savedSearches}
          {...pageProps}
        />
      ),
    },
    {
      title: `You're All Set`,
      skippable: true,
      component: pageProps => (
        <ThankYouPage
          featureFlags={authProps.featureFlags}
          user={user}
          elmId='signup_form'
          redirectToSearch={redirectToSearch}
          onClick={redirectToSearch ? redirectToSavedSearch : successfulSignup}
          {...pageProps}
        />
      ),
    },
  ]
  return (
    <>
      <StepForm header={OnboardingHeader} pages={pages} />
    </>
  )
}

AuthToggleStepForm.propTypes = {
  redirectToSearch: PropTypes.bool,
  userKind: PropTypes.oneOf(Object.values(USER_KINDS)),
  signUpInitialValues: PropTypes.object,
}

AuthToggleStepForm.USER_KINDS = USER_KINDS

export default AuthToggleStepForm
